// Phase 43-09 — <CanvasEnginesSection /> (CANVAS-01 gap closure).
// Re-implements the engines field-array. The wizard uses
// useFieldArray('engines') at SpecsAndConfigStep.tsx:132. We mirror that
// pattern using react-hook-form's library API (NOT a wizard-internal
// primitive — D-15 (V1 era): the wizard folder was never modified by the canvas work).
//
// #1307 — every engine input is framed by EngineFieldFrame: the same tint /
// chip / ConflictChooser / FieldNotices envelope a boat field gets, keyed by
// the projection's `engine#N:column` (see lib/engineProvenanceKey.ts).
//
// Typography lock (UI-SPEC rev 2 — strict 2-weight 400/600 palette, 13px
// floor for new components): 13px labels font-normal, 14px input body
// font-normal. The 12px size and 500-weight Tailwind utility class names
// are disallowed by UI-SPEC and appear nowhere in this file.

import type { ReactNode } from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { EngineMakeCombobox } from '../fields/EngineMakeCombobox';
import { Plus, X } from 'lucide-react';
import { Button } from '@marine/ui/button';
import { Input, InputAddon, InputGroup } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { FieldTintWrapper } from '../FieldTintWrapper';
import { ProvenanceChip } from '../ProvenanceChip';
import { ConflictChooser } from '../ConflictChooser';
import { FieldNotices } from '../CanvasReview';
import { useProvenance } from '../hooks/useProvenance';
import { sortByAuthority } from '../lib/sourceAuthority';
import { engineProvenanceKey, type EngineProvenanceColumn } from '../lib/engineProvenanceKey';
import type { ProvenanceEntry } from '@/modules/inventory/types/boat.types';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';

// Phase 45.5.3 (CANVAS-14) — shared numeric coercion for HP / Hours / Year:
// '' | null | undefined → undefined; else Number(v). Keeps the per-engine
// subobject free of NaN and matches the prior setValueAs contract.
const numericSetValueAs = (v: unknown) =>
  v === '' || v === null || v === undefined ? undefined : Number(v);

function EngineFieldFrame({ index, column, label, htmlFor, children }: {
  index: number;
  column: EngineProvenanceColumn;
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  const fieldName = engineProvenanceKey(index, column);
  const { state, clear, selectSource } = useProvenance(fieldName);
  return (
    <FieldTintWrapper fieldName={fieldName} className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={htmlFor} className="text-[0.8125rem] font-normal">
          {label}
        </Label>
        <ProvenanceChip
          fieldName={fieldName}
          entry={state.entry}
          onClear={clear}
          isLowConfidence={state.isLowConfidence}
        />
      </div>
      {state.isConflict && Array.isArray(state.entry) && (
        <ConflictChooser
          fieldName={fieldName}
          variant="radio"
          entries={sortByAuthority(state.entry as ProvenanceEntry[])}
          selectedIndex={state.pickedIndex}
          onSelectOne={(entry) => selectSource(entry)}
        />
      )}
      {children}
      <FieldNotices fieldName={fieldName} />
    </FieldTintWrapper>
  );
}

export function CanvasEnginesSection() {
  const { control, register, getValues, setValue } = useFormContext<WizardFormData>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'engines',
  });

  // #1307 — provenance is keyed by link position (`engine#N:…`) and an unsaved
  // delete shifts the rows up, so renumber the map with the rows: drop the
  // removed row's keys, move every later row's keys down one.
  const handleRemove = (idx: number) => {
    const current = (getValues() as unknown as { _provenance?: Record<string, unknown> })._provenance ?? {};
    const next: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(current)) {
      const match = /^engine#(\d+):(.+)$/.exec(key);
      if (!match) {
        next[key] = entry;
        continue;
      }
      const n = Number(match[1]);
      if (n === idx) continue;
      next[n > idx ? `engine#${n - 1}:${match[2]}` : key] = entry;
    }
    remove(idx);
    setValue('_provenance' as never, next as never, { shouldDirty: false });
  };

  const handleAdd = () => {
    append({});
  };

  return (
    <div className="space-y-3">
      {fields.length === 0 ? (
        <p className="text-[0.8125rem] font-normal text-muted-foreground">
          No engines added.
        </p>
      ) : (
        <div className="space-y-3">
          {fields.map((field, idx) => {
            const mfrId = `canvas-engine-${idx}-manufacturer`;
            const modelId = `canvas-engine-${idx}-model`;
            const hpId = `canvas-engine-${idx}-horsepower`;
            const yearId = `canvas-engine-${idx}-year`;
            const hoursId = `canvas-engine-${idx}-hours`;
            const serialId = `canvas-engine-${idx}-serial`;
            return (
              <div
                key={field.id}
                className="rounded-md border border-border p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[0.8125rem] font-normal text-foreground">
                    Engine {idx + 1}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label="Remove engine"
                    onClick={() => handleRemove(idx)}
                  >
                    <X className="size-3.5 me-1" />
                    Remove
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Manufacturer + Model */}
                  <EngineFieldFrame index={idx} column="engine_manufacturer" label="Manufacturer" htmlFor={mfrId}>
                    <Controller
                      control={control}
                      name={`engines.${idx}.engine_manufacturer`}
                      render={({ field }) => (
                        <EngineMakeCombobox
                          id={mfrId}
                          value={field.value ?? ''}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </EngineFieldFrame>
                  <EngineFieldFrame index={idx} column="engine_model" label="Model" htmlFor={modelId}>
                    <Input
                      id={modelId}
                      type="text"
                      className="text-sm font-normal"
                      {...register(`engines.${idx}.engine_model`)}
                    />
                  </EngineFieldFrame>
                  {/* Horsepower (HP addon) + Year */}
                  <EngineFieldFrame index={idx} column="engine_horsepower" label="Horsepower" htmlFor={hpId}>
                    <InputGroup>
                      <Input
                        id={hpId}
                        type="number"
                        className="text-sm font-normal"
                        {...register(`engines.${idx}.engine_horsepower`, {
                          setValueAs: numericSetValueAs,
                        })}
                      />
                      <InputAddon>HP</InputAddon>
                    </InputGroup>
                  </EngineFieldFrame>
                  <EngineFieldFrame index={idx} column="engine_year" label="Year" htmlFor={yearId}>
                    <Input
                      id={yearId}
                      type="number"
                      className="text-sm font-normal"
                      {...register(`engines.${idx}.engine_year`, {
                        setValueAs: numericSetValueAs,
                      })}
                    />
                  </EngineFieldFrame>
                  {/* Hours (hrs addon) + Serial Number */}
                  <EngineFieldFrame index={idx} column="engine_hours" label="Hours" htmlFor={hoursId}>
                    <InputGroup>
                      <Input
                        id={hoursId}
                        type="number"
                        className="text-sm font-normal"
                        {...register(`engines.${idx}.engine_hours`, {
                          setValueAs: numericSetValueAs,
                        })}
                      />
                      <InputAddon>hrs</InputAddon>
                    </InputGroup>
                  </EngineFieldFrame>
                  <EngineFieldFrame index={idx} column="engine_serial_number" label="Serial Number" htmlFor={serialId}>
                    <Input
                      id={serialId}
                      type="text"
                      className="text-sm font-normal"
                      {...register(`engines.${idx}.engine_serial_number`)}
                    />
                  </EngineFieldFrame>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAdd}
      >
        <Plus className="size-3.5 me-1" />
        Add engine
      </Button>
    </div>
  );
}
